/**
 * Canvas画像表示コンポーネント（共通）
 *
 * ProcessedImageDisplayから描画ロジックを抽出した共通コンポーネント
 * BlobまたはURLから画像をCanvas描画し、高品質表示を実現
 *
 * 特徴:
 * - CSP対応（renderBlobToCanvasNoURL使用）
 * - デバイスピクセル比対応（高解像度ディスプレイ）
 * - リサイズ対応（コンテナに収まるように自動調整）
 * - ダウンロードボタン（オプション）
 */

import React, { useRef, useEffect, useState } from 'react';
import { renderBlobToCanvasNoURL } from '../../../utils/blobToCanvas';
import { AfterImageDownloadIcon } from '../../SVGIcons';
import { compressImage, CompressionPresets } from '../../../utils/imageCompression';
import { ImageEditingState } from '../../../reducers/imageEditing';

export interface CanvasImageDisplayProps {
  imageSource: Blob | string;  // BlobまたはURL
  onDownload?: () => void;
  className?: string;
  showDownloadButton?: boolean;
  showEditButton?: boolean;    // 編集ボタンの表示（オプション）
  onEditClick?: () => void;    // 編集ボタンクリック時のコールバック（オプション）
  maxWidth?: number;
  maxHeight?: number;
  uploadedImages?: any[];  // ダウンロード時のファイル名生成用（オプション）
  originalId?: string;     // ダウンロード時のファイル名生成用（オプション）
  features?: ImageEditingState['features'];  // 圧縮設定用（オプション）
}

const CanvasImageDisplay: React.FC<CanvasImageDisplayProps> = (props) => {
  const {
    imageSource,
    onDownload,
    className = '',
    showDownloadButton = true,
    showEditButton = false,
    onEditClick,
    maxWidth,
    maxHeight,
    uploadedImages,
    originalId,
    features,
  } = props;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);

  // 画像ソースをBlobに変換
  useEffect(() => {
    console.log('[CanvasImageDisplay] imageSource changed:',
      imageSource instanceof Blob ? `Blob(${imageSource.size})` : typeof imageSource === 'string' ? `URL(${imageSource.substring(0, 50)}...)` : 'null');

    const convertToBlob = async () => {
      if (imageSource instanceof Blob) {
        console.log('[CanvasImageDisplay] Setting imageBlob from Blob:', imageSource.size);
        setImageBlob(imageSource);
      } else if (typeof imageSource === 'string') {
        try {
          const response = await fetch(imageSource);
          const blob = await response.blob();
          console.log('[CanvasImageDisplay] Setting imageBlob from URL fetch:', blob.size);
          setImageBlob(blob);
        } catch (error) {
          console.error('Failed to fetch image:', error);
          setIsLoading(false);
        }
      }
    };

    convertToBlob();
  }, [imageSource]);

  // Canvas描画処理
  useEffect(() => {
    if (!canvasRef.current || !containerRef.current || !imageBlob) {
      console.log('[CanvasImageDisplay] Canvas render skipped:', {
        hasCanvas: !!canvasRef.current,
        hasContainer: !!containerRef.current,
        hasImageBlob: !!imageBlob
      });
      return;
    }

    console.log('[CanvasImageDisplay] Starting canvas render for blob:', imageBlob.size);
    setIsLoading(true);

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setIsLoading(false);
      return;
    }

    // 親要素のサイズを取得
    const { parentElement } = containerRef.current;
    const containerWidth = parentElement
      ? parentElement.clientWidth - 24  // p-3のパディング考慮
      : maxWidth || 800;
    const containerHeight = parentElement
      ? parentElement.clientHeight - 24
      : maxHeight || 600;

    // CSP対応のCanvas描画を使用（高品質設定）
    const largeSize = 3000; // 十分大きな値

    renderBlobToCanvasNoURL(imageBlob, canvas, {
      maxWidth: largeSize,
      maxHeight: largeSize,
    })
      .then(() => {
        // 実際の画像サイズを取得
        const actualWidth = canvas.width;
        const actualHeight = canvas.height;

        // デバイスピクセル比を考慮して高解像度レンダリング
        const dpr = window.devicePixelRatio || 1;

        // 高解像度キャンバスを作成
        const hdCanvas = document.createElement('canvas');
        hdCanvas.width = actualWidth * dpr;
        hdCanvas.height = actualHeight * dpr;
        const hdCtx = hdCanvas.getContext('2d');

        if (hdCtx) {
          // 高解像度で描画
          hdCtx.scale(dpr, dpr);
          hdCtx.drawImage(canvas, 0, 0);

          // 元のキャンバスに高解像度データをコピー
          canvas.width = hdCanvas.width;
          canvas.height = hdCanvas.height;
          ctx.drawImage(hdCanvas, 0, 0);
        }

        // 表示サイズを計算（コンテナに収まるように調整）
        let displayWidth = actualWidth;
        let displayHeight = actualHeight;

        if (displayWidth > containerWidth || displayHeight > containerHeight) {
          const scale = Math.min(
            containerWidth / displayWidth,
            containerHeight / displayHeight
          );
          displayWidth *= scale;
          displayHeight *= scale;
        }

        canvas.style.width = `${displayWidth}px`;
        canvas.style.height = `${displayHeight}px`;
        setDimensions({ width: displayWidth, height: displayHeight });
        console.log('[CanvasImageDisplay] Canvas render complete, display size:', displayWidth, 'x', displayHeight);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Canvas rendering failed:', error);
        setIsLoading(false);
      });
  }, [imageBlob, maxWidth, maxHeight]);

  // ウィンドウリサイズ時の再描画
  useEffect(() => {
    const handleResize = () => {
      if (canvasRef.current && containerRef.current && imageBlob && !isLoading) {
        const { parentElement } = containerRef.current;
        const containerWidth = parentElement ? parentElement.clientWidth - 24 : maxWidth || 800;
        const containerHeight = parentElement ? parentElement.clientHeight - 24 : maxHeight || 600;

        const canvas = canvasRef.current;
        const largeSize = 3000;

        renderBlobToCanvasNoURL(imageBlob, canvas, {
          maxWidth: largeSize,
          maxHeight: largeSize,
        }).then(() => {
          const actualWidth = canvas.width;
          const actualHeight = canvas.height;

          // 表示サイズを計算
          let displayWidth = actualWidth;
          let displayHeight = actualHeight;

          if (displayWidth > containerWidth || displayHeight > containerHeight) {
            const scale = Math.min(
              containerWidth / displayWidth,
              containerHeight / displayHeight
            );
            displayWidth *= scale;
            displayHeight *= scale;
          }

          canvas.style.width = `${displayWidth}px`;
          canvas.style.height = `${displayHeight}px`;
          setDimensions({ width: displayWidth, height: displayHeight });
        });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [imageBlob, isLoading, maxWidth, maxHeight]);

  // ダウンロード処理
  const handleDownload = async () => {
    if (onDownload) {
      onDownload();
      return;
    }

    if (!imageBlob) return;

    let blobToDownload = imageBlob;

    // 圧縮設定が有効な場合は圧縮を適用
    if (features?.compression?.enabled && features.compression.settings) {
      try {
        console.log('[CanvasImageDisplay] Compression enabled, compressing image...');

        // BlobからFileオブジェクトを作成
        const file = new File([imageBlob], 'temp.jpg', { type: imageBlob.type });

        // 圧縮設定を取得
        const compressionSettings = features.compression.settings;
        const targetSize = compressionSettings.targetSize === '1MB'
          ? CompressionPresets.ONE_MB
          : CompressionPresets.FIVE_MB;

        // 画像を圧縮
        const compressionResult = await compressImage(file, {
          targetSize,
          format: 'jpeg',
          initialQuality: 0.9,
          resizeEnabled: false,
        });

        blobToDownload = compressionResult.blob;
        console.log('[CanvasImageDisplay] Compression complete:',
          `${(imageBlob.size / 1024 / 1024).toFixed(2)}MB -> ${(compressionResult.compressedSize / 1024 / 1024).toFixed(2)}MB`);
      } catch (error) {
        console.error('Compression failed:', error);
        // 圧縮に失敗した場合は元のBlobを使用
      }
    }

    // ダウンロード実行
    const url = URL.createObjectURL(blobToDownload);
    const a = document.createElement('a');
    a.href = url;

    // ファイル名を生成
    let fileName = 'processed_image.jpg';

    if (uploadedImages && originalId) {
      const originalImage = uploadedImages.find(img => img.id === originalId);
      if (originalImage && originalImage.name) {
        const baseName = originalImage.name.replace(/\.[^/.]+$/, '');
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:\-T]/g, '');
        fileName = `${baseName}_${timestamp}.jpg`;
      }
    }

    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      ref={containerRef}
      className={`w-full h-full flex items-center justify-center p-3 ${className}`}
    >
      <div className="relative group max-w-full max-h-full">
        <div className="duration-100">
          {/* 編集ボタンとダウンロードボタン */}
          {(showDownloadButton || showEditButton) && (
            <div className="absolute -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 z-30 flex items-center gap-3">
              {/* 編集ボタン */}
              {showEditButton && (
                <button
                  onClick={onEditClick}
                  className="w-[72px] h-[72px] rounded-full bg-white/95 hover:bg-white/90 hidden group-hover:flex flex-col justify-center items-center z-30"
                  disabled={isLoading}
                  type="button"
                  title="編集"
                >
                  <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  <p className="text-sm text-center">編集</p>
                </button>
              )}
              {/* ダウンロードボタン */}
              {showDownloadButton && (
                <button
                  onClick={handleDownload}
                  className="w-[72px] h-[72px] rounded-full bg-white/95 hover:bg-white/90 hidden group-hover:flex flex-col justify-center items-center z-30"
                  disabled={isLoading}
                  type="button"
                  title="画像をダウンロード"
                >
                  <AfterImageDownloadIcon className="w-6 h-6 mb-1" />
                  <p className="text-sm text-center">保存</p>
                </button>
              )}
            </div>
          )}

          {/* ホバー時のオーバーレイ */}
          {showDownloadButton && (
            <span className="absolute group-hover:inset-0 group-hover:bg-gray-500/60 z-10 rounded-lg" />
          )}

          {/* ローディング表示 */}
          {isLoading && (
            <div className="flex justify-center items-center h-32">
              <span className="text-gray-500">画像を読み込み中...</span>
            </div>
          )}

          {/* Canvas要素 */}
          <canvas
            ref={canvasRef}
            className={`rounded-lg shadow-lg ${
              isLoading ? 'opacity-0' : 'opacity-100'
            } transition-opacity block max-w-full max-h-full`}
            style={{
              imageRendering: 'high-quality',
              display: isLoading ? 'none' : 'block',
              objectFit: 'contain',
            }}
          />
        </div>
      </div>
    </div>
  );
};

CanvasImageDisplay.displayName = 'CanvasImageDisplay';

export default CanvasImageDisplay;
